export const PLATFORM_FEE_RATE = 0.05

/** Stripe standard US card processing — display copy only (not used in charge math). */
export const STRIPE_PROCESSING_FEE_LABEL = '2.9% + $0.30'

export function platformFeeCents(amountCents) {
  if (typeof amountCents !== 'number') return 0
  return Math.round(amountCents * PLATFORM_FEE_RATE)
}

export function inferDepositTimeSurchargeCents(
  amountCents,
  paidAmountCents,
  paymentMode = 'deposit'
) {
  if (paymentMode !== 'deposit') return 0
  if (typeof paidAmountCents !== 'number') return 0
  return Math.max(0, paidAmountCents - depositDueCents(amountCents, 0))
}

/** Surcharges already collected on the deposit (webhook field or inferred from paid amount). */
export function effectiveDepositTimeSurchargeCents(
  amountCents,
  depositTimeSurchargeCents = 0,
  paidAmountCents,
  paymentMode = 'deposit'
) {
  return Math.max(
    typeof depositTimeSurchargeCents === 'number' ? depositTimeSurchargeCents : 0,
    inferDepositTimeSurchargeCents(amountCents, paidAmountCents, paymentMode)
  )
}

/** Merge pending, saved, and deposit-paid time surcharges for display totals. */
export function effectiveReservationSurchargeCents(
  currentSurchargeCents = 0,
  savedSurchargeCents = 0,
  options = {}
) {
  const {
    paymentMode,
    depositPaid = false,
    depositTimeSurchargeCents = 0,
    paidAmountCents,
    amountCents = 0,
  } = options

  let cents = Math.max(
    typeof currentSurchargeCents === 'number' ? currentSurchargeCents : 0,
    typeof savedSurchargeCents === 'number' ? savedSurchargeCents : 0
  )

  if (depositPaid && paymentMode === 'deposit') {
    cents = Math.max(
      cents,
      typeof depositTimeSurchargeCents === 'number'
        ? depositTimeSurchargeCents
        : 0,
      inferDepositTimeSurchargeCents(
        amountCents,
        paidAmountCents,
        paymentMode
      )
    )
  }

  return cents
}

export function reservationTotalCents(amountCents, timeSurchargeCents = 0) {
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  return base + platformFeeCents(base) + surcharges
}

export function depositDueCents(amountCents, timeSurchargeCents = 0) {
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  return Math.round(base * 0.5) + platformFeeCents(base) + surcharges
}

export function balanceDueCents(
  amountCents,
  timeSurchargeCents = 0,
  options = {}
) {
  const { depositPaid = false, depositTimeSurchargeCents = 0 } = options
  const base = typeof amountCents === 'number' ? amountCents : 0
  const surcharges =
    typeof timeSurchargeCents === 'number' ? timeSurchargeCents : 0
  const half = Math.round(base * 0.5)

  if (!depositPaid) {
    return half
  }

  const paidSurcharges = effectiveDepositTimeSurchargeCents(
    base,
    depositTimeSurchargeCents,
    options.paidAmountCents,
    options.paymentMode || 'deposit'
  )
  const additionalSurcharges = Math.max(0, surcharges - paidSurcharges)
  return half + additionalSurcharges
}

export function initialDepositChargeCents(
  paymentMode,
  amountCents,
  timeSurchargeCents = 0
) {
  if (paymentMode === 'deposit') {
    return depositDueCents(amountCents, timeSurchargeCents)
  }
  return reservationTotalCents(amountCents, timeSurchargeCents)
}

/**
 * Remaining balance for deposit reservations after the deposit is paid.
 * Derived from reservation total minus paid deposit so surcharges on the
 * deposit are never counted again on the balance.
 */
export function reservationBalanceDueCents(
  amountCents,
  timeSurchargeCents = 0,
  options = {}
) {
  const {
    depositPaid = false,
    paymentMode = 'deposit',
    depositTimeSurchargeCents = 0,
    paidAmountCents,
    savedTimeSurchargeCents = 0,
  } = options

  if (!depositPaid || paymentMode !== 'deposit') {
    return balanceDueCents(amountCents, timeSurchargeCents, {
      depositPaid,
      depositTimeSurchargeCents,
      paidAmountCents,
      paymentMode,
    })
  }

  const surcharges = effectiveReservationSurchargeCents(
    timeSurchargeCents,
    savedTimeSurchargeCents,
    {
      paymentMode,
      depositPaid: true,
      depositTimeSurchargeCents,
      paidAmountCents,
      amountCents,
    }
  )
  const total = reservationTotalCents(amountCents, surcharges)
  const paidDeposit =
    paidAmountCents ?? depositDueCents(amountCents, surcharges)
  return Math.max(0, total - paidDeposit)
}
